import api from '../../../api/apiClient';
import { ApiEnvelope, unwrapEnvelope } from '../../../api/resource';
import { JobMilestonePayload } from '../domain/mappers/jobQuote.mapper';
import { financialYearLabel } from '../../shared/utils';

/**
 * What the report filters on: the nine work_jobs foreign keys plus `q`, the free-text
 * search. `''` means "no filter" for all of them.
 */
export interface WorkJobReportFilters {
  /** Live search across every text column the report shows. ANDs with the dropdowns. */
  q?: string;
  project_type_id?: string;
  construction_work_type_id?: string;
  building_type_id?: string;
  status_id?: string;
  financial_year_id?: string;
  client_id?: string;
  architect_id?: string;
  builder_id?: string;
  owner_id?: string;
}

export type WorkJobFilterKey = keyof WorkJobReportFilters;

/** The dropdown filters alone — the search box is keyed separately. */
export type WorkJobDropdownFilterKey = Exclude<WorkJobFilterKey, 'q'>;

export interface FilterOption {
  id: number;
  label: string;
}

/**
 * Financial years carry their range as well as their label, so a job date can be matched
 * to its year without assuming how the label is spelled — the seed uses "2026-2027" but
 * the entity documents "2026-27".
 */
export interface FinancialYearOption extends FilterOption {
  start_date: string;
  end_date: string;
}

/**
 * The financial year a job date falls in: 1 April to 31 March, so 2027-02-02 is
 * 2026-2027 and 2026-02-02 is 2025-2026.
 *
 * Prefers the row whose range contains the date, so what the dialog shows is the row the
 * server will store. Falls back to the derived label for a date past the last seeded year
 * — the server auto-creates exactly that year on save.
 */
export function financialYearForDate(
  jobDate: string,
  financialYears: FinancialYearOption[]
): { id: number | null; label: string } | null {
  if (!jobDate) return null;

  // All three are ISO `yyyy-mm-dd`, which compares correctly as text.
  const match = financialYears.find(
    (year) =>
      year.start_date && year.end_date && jobDate >= year.start_date && jobDate <= year.end_date
  );
  if (match) return { id: match.id, label: match.label };

  const derived = financialYearLabel(jobDate);
  return derived ? { id: null, label: derived } : null;
}

/** One option list per dropdown, keyed the way `/filters` returns them. */
export interface WorkJobReportFilterOptions {
  project_types: FilterOption[];
  construction_work_types: FilterOption[];
  building_types: FilterOption[];
  statuses: FilterOption[];
  financial_years: FinancialYearOption[];
  /** Not a report filter — the edit dialog's locality picker reads it from here (V12). */
  localities: FilterOption[];
  clients: FilterOption[];
  architects: FilterOption[];
  builders: FilterOption[];
  owners: FilterOption[];
  /** V19 — active companies only, for the Add Project picker. */
  companies: FilterOption[];
}

export const EMPTY_FILTER_OPTIONS: WorkJobReportFilterOptions = {
  project_types: [],
  construction_work_types: [],
  building_types: [],
  statuses: [],
  financial_years: [],
  localities: [],
  clients: [],
  architects: [],
  builders: [],
  owners: [],
  companies: [],
};

export interface WorkJobReportRow {
  id: number;
  job_code: string;
  job_name: string;
  job_date: string | null;
  financial_year: string | null;
  project_type: string | null;
  construction_work_type: string | null;
  building_type: string | null;
  status: string | null;
  client_name: string | null;
  architect_name: string | null;
  builder_name: string | null;
  owner_name: string | null;
  locality: string | null;
  site_address: string | null;
  job_area: number | null;
  /** V17 — the building's own total area, set on the Work Details screen. */
  total_area_sqft: number | null;
  total_cost: number | null;
  total_after_discount: number | null;
  gross_total: number | null;
  milestone_count: number | null;
  milestones_completed: number | null;
  /** Raw foreign keys, sent alongside the labels so the edit dialog preselects by id. */
  financial_year_id: number | null;
  locality_id: number | null;
  project_type_id: number | null;
  construction_work_type_id: number | null;
  building_type_id: number | null;
  status_id: number | null;
  client_id: number | null;
  architect_id: number | null;
  builder_id: number | null;
  owner_id: number | null;
  /** V19 — the firm's company the job is taken under; null on older jobs. */
  company_id: number | null;
  /** Sent with the id: the picker lists active companies only, and this one may not be. */
  company_name: string | null;
}

interface WorkJobReportResponse {
  success: boolean;
  data: WorkJobReportRow[];
  count: number;
}

/** Drops cleared dropdowns so the query string only carries filters the user actually set. */
function toParams(filters: WorkJobReportFilters): Record<string, string> {
  return Object.entries(filters).reduce<Record<string, string>>((acc, [key, value]) => {
    if (value !== undefined && value !== null && value !== '') acc[key] = String(value);
    return acc;
  }, {});
}

/** All nine dropdowns in one request — the page would otherwise open with nine. */
export async function getWorkJobReportFilterOptions(): Promise<WorkJobReportFilterOptions> {
  const res = await api.get<ApiEnvelope<WorkJobReportFilterOptions>>('/api/reports/work-jobs/filters');
  return unwrapEnvelope(res.data) ?? EMPTY_FILTER_OPTIONS;
}

export async function getWorkJobReport(filters: WorkJobReportFilters): Promise<WorkJobReportRow[]> {
  const res = await api.get<WorkJobReportResponse>('/api/reports/work-jobs', {
    params: toParams(filters),
  });
  return res.data?.data ?? [];
}

/** Reads the filename the server chose, falling back to a dated default. */
function filenameFrom(disposition: unknown): string {
  const match = typeof disposition === 'string' ? /filename="?([^"]+)"?/.exec(disposition) : null;
  return match?.[1] ?? `work-jobs-report-${new Date().toISOString().slice(0, 10)}.xlsx`;
}

/** Recovers the API's error message from a blob-typed error response. */
async function withBlobMessage(error: any): Promise<any> {
  const body = error?.response?.data;
  if (body instanceof Blob) {
    try {
      const parsed = JSON.parse(await body.text());
      error.userMessage = parsed?.message || parsed?.error || error.userMessage;
    } catch {
      // Not JSON after all — leave the interceptor's message in place.
    }
  }
  return error;
}

/**
 * Downloads the current filter selection as .xlsx. The export takes the same query
 * string as the list, so the workbook is always the table on screen — never the whole
 * table when a filter is on.
 */
export async function downloadWorkJobReportExcel(filters: WorkJobReportFilters): Promise<void> {
  const res = await api.get<Blob>('/api/reports/work-jobs/export', {
    params: toParams(filters),
    responseType: 'blob',
  }).catch(async (error: any) => {
    // A failed export still arrives as a Blob, so the interceptor never saw the
    // JSON error body. Read it here or the user gets a bare "Network Error".
    throw await withBlobMessage(error);
  });

  const url = URL.createObjectURL(res.data);
  const link = document.createElement('a');
  link.href = url;
  link.download = filenameFrom(res.headers?.['content-disposition']);
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/* ── Editing a job from the report ────────────────────────────────────────── */

/**
 * The subset of `PUT /api/analyst/job/{id}/update` the report's edit dialog sends.
 *
 * The three lookups travel as **labels**, not ids: `LookupService.resolveProjectType`
 * and friends match on label first and fall back to a code lookup. Sending a label
 * straight off the filter dropdown is the only value guaranteed to resolve.
 *
 * Every key is optional and the controller only touches keys that are present, so a
 * dialog that sends six fields leaves the job's quote, floors, milestones and files
 * untouched.
 */
export interface WorkJobUpdatePayload {
  /**
   * Payment milestones. The controller replaces the job's whole set when this key is
   * present, so it is always the complete table — never a partial edit. A row's status is
   * carried over by milestone number where the request does not state one.
   */
  milestones?: JobMilestonePayload[];
  milestone_count?: number;
  job_code?: string;
  job_name?: string;
  job_date?: string;
  /** Locality **label** — resolved against the `localities` master (V12) server-side. */
  locality?: string;
  site_address?: string;
  /** Project type **label**, e.g. "New Job". */
  job_type?: string;
  /** Construction work type **label**, e.g. "Allied Work". */
  job_work_type?: string;
  /** Building type **label**. Unknown values are auto-created server-side. */
  building_type?: string;
  client_id?: number;
  architect_id?: number;
  builder_id?: number;
  owner_id?: number;
  /** V19 — the firm's company the job is taken under. */
  company_id?: number;
}

export async function updateWorkJob(
  jobId: number,
  payload: WorkJobUpdatePayload
): Promise<void> {
  await api.put(`/api/analyst/job/${jobId}/update`, payload);
}

/** A milestone as `GET /api/analyst/job/{id}` returns it. */
export interface JobMilestoneRow {
  milestone_number: number;
  payment_percent: number | string | null;
  milestone: string | null;
  milestone_status: string | null;
}

/**
 * The job's saved milestones, so the QUOTATION panel opens on the table that is stored
 * rather than on the blank four-row default.
 *
 * Read from the job detail endpoint rather than a milestones-only one: that endpoint
 * already assembles them, and adding a second route for the same rows would give the two
 * screens two definitions of what a milestone is.
 */
export async function getJobMilestones(jobId: number | string): Promise<JobMilestoneRow[]> {
  const res = await api.get<{ data?: { milestones?: JobMilestoneRow[] } }>(
    `/api/analyst/job/${jobId}`
  );
  return res.data?.data?.milestones ?? [];
}

/**
 * Replaces the job's milestones. Sent on its own so the QUOTATION panel touches nothing
 * else on the job — `updateJob` only acts on the keys a request actually carries.
 */
export async function saveJobMilestones(
  jobId: number | string,
  milestones: JobMilestonePayload[]
): Promise<void> {
  await api.put(`/api/analyst/job/${jobId}/update`, {
    milestones,
    milestone_count: milestones.length,
  });
}

/**
 * Status moves through its own endpoint — `updateJob` has no status branch at all.
 * `newStatus` must be the status **label**; the server special-cases exactly
 * "Canceled" to also store a cancellation reason.
 */
export async function updateWorkJobStatus(
  jobId: number,
  newStatus: string,
  cancellationReason?: string
): Promise<void> {
  await api.put(`/api/analyst/job/${jobId}/status`, {
    new_status: newStatus,
    ...(cancellationReason ? { cancellation_reason: cancellationReason } : {}),
  });
}

/* ── Creating a job with the edit screen's field set ───────────────────────── */

/**
 * The body `POST /api/analyst/job/create` needs for a job carrying exactly the fields the
 * report's Edit Job screen covers. Lookups travel as **labels** and contacts as **ids**,
 * the same contract as {@link WorkJobUpdatePayload}.
 *
 * The endpoint was written for the full seven-step wizard, and two of its defaults matter
 * here: an absent `job_type` / `job_work_type` resolves to "New Job" / "Allied Work"
 * rather than null, and an absent `job_status` to "Not Started".
 */
export interface WorkJobCreatePayload extends WorkJobUpdatePayload {
  job_code: string;
  job_name: string;
  job_date: string;
  site_address: string;
  locality: string;
  /** Status **label**, e.g. "Not Started". */
  job_status?: string;
  /** Only meaningful alongside `job_status: "Canceled"`. */
  cancellation_reason?: string;
  /**
   * `work_jobs.milestone_count` is NOT NULL and the endpoint does not default it — a
   * create without this fails at the database. This screen has no milestones, so 0.
   */
  milestone_count: number;
}

export async function createWorkJob(payload: WorkJobCreatePayload): Promise<number> {
  const res = await api.post<{ status: boolean; job_id: number }>('/api/analyst/job/create', payload);
  return res.data.job_id;
}
